import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import BulkEmailModal from "@/components/admin/BulkEmailModal";
import { Archive, ArchiveRestore, Trash2, Plus, X, Mail, Edit3, CalendarClock } from "lucide-react";

// Sprint 92t → 92af — Pipeline candidatures reconfiguré en 6 onglets logiques :
// Nouvelles → Approuvées → Refusées → Facturées → Relancées → Archivées.
// Approuvées = acceptée par un admin et en attente de facturation (sans
// invoice_sent). Facturées = lien de paiement envoyé, non payé, pas (encore)
// relancé. Relancées = `last_reminder_at` défini (manuel ou cron automatique
// 7 jours après invoice_sent). Archivées = payées / actives / archivées.
const matchTab = (a, tab) => {
  const s = a.status;
  const archivedFlag = a.archived === true;
  const terminalPaid = s === "paid" || s === "active";
  if (tab === "all") return !archivedFlag && !terminalPaid;
  if (tab === "archived") return archivedFlag || terminalPaid;
  if (tab === "rejected") return s === "rejected";
  if (archivedFlag || terminalPaid) return false; // other tabs are "active" only
  if (tab === "relaunched") {
    return !!a.last_reminder_at && ["invoice_draft", "billing_received", "invoice_sent"].includes(s);
  }
  if (tab === "invoiced") {
    return ["invoice_draft", "billing_received", "invoice_sent"].includes(s) && !a.last_reminder_at;
  }
  if (tab === "approved") return ["approved", "accepted"].includes(s);
  if (tab === "new") return ["new", "submitted", "under_review"].includes(s);
  return false;
};
const STATUSES = [
  { id: "new", l: "Nouvelles" },
  { id: "approved", l: "Approuvées" },
  { id: "rejected", l: "Refusées" },
  { id: "invoiced", l: "Facturées" },
  { id: "relaunched", l: "Relancées" },
  { id: "archived", l: "Archivées" },
  { id: "all", l: "Toutes" },
];

export default function AdminApplications() {
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("new");
  const [selected, setSelected] = useState([]);
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [editApp, setEditApp] = useState(null);

  // Always load active + archived in parallel; filter client-side via matchTab.
  const load = async () => {
    try {
      const [a1, a2] = await Promise.all([
        api.get("/applications", { params: { archived: false } }),
        api.get("/applications", { params: { archived: true } }),
      ]);
      const archivedDocs = (a2.data || []).map((d) => ({ ...d, archived: true }));
      setApps([...(a1.data || []), ...archivedDocs]);
      setSelected([]);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const upd = async (id, status) => {
    try {
      await api.patch(`/applications/${id}`, { status });
      const isArchiving = status === "approved" || status === "rejected";
      toast.success(isArchiving ? "Statut mis à jour — candidature archivée" : "Statut mis à jour");
      load();
    } catch { toast.error("Erreur"); }
  };

  const unarchive = async (id) => {
    try {
      await api.patch(`/applications/${id}`, { status: "under_review", archived: false });
      toast.success("Candidature désarchivée");
      load();
    } catch { toast.error("Erreur"); }
  };

  const del = async (a) => {
    if (!window.confirm(`Supprimer définitivement la candidature de ${a.company_name} ?`)) return;
    try { await api.delete(`/applications/${a.id}`); toast.success("Candidature supprimée"); load(); }
    catch { toast.error("Erreur"); }
  };

  const sendReminder = async (a) => {
    const nextCount = (a.reminders_count || 0) + 1;
    if (!window.confirm(`Envoyer un email de relance paiement à ${a.contact_name || a.company_name} (${a.email}) ?\n\n${a.reminders_count ? `Ce sera la relance n°${nextCount}.` : "Première relance."}`)) return;
    try {
      await api.post(`/applications/${a.id}/send-reminder`);
      toast.success("Email de relance envoyé ✉️");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi");
    }
  };

  const list = apps.filter((a) => matchTab(a, filter));

  const toggleSelect = (id) => setSelected((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  const toggleSelectAll = (e) => setSelected(e.target.checked ? list.map((a) => a.id) : []);
  const allChecked = list.length > 0 && selected.length === list.length;

  // ---- Manual creation ----
  const [createOpen, setCreateOpen] = useState(false);
  const emptyForm = () => ({
    company_name: "", first_name: "", last_name: "", email: "", phone: "",
    pole: "", category: "cat-startup", website: "", linkedin: "", description: "",
    annual_revenue: "", country: "France",
  });
  const [form, setForm] = useState(emptyForm());
  const [creating, setCreating] = useState(false);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.email.trim()) {
      return toast.error("Société et email sont obligatoires");
    }
    if (!form.description.trim()) form.description = form.company_name;
    if (!form.category.trim()) form.category = "cat-startup";
    if (!form.country.trim()) form.country = "France";
    setCreating(true);
    try {
      const payload = { ...form, contact_name: `${form.first_name} ${form.last_name}`.trim() || form.company_name };
      if (form.annual_revenue) payload.annual_revenue = parseFloat(form.annual_revenue);
      else delete payload.annual_revenue;
      await api.post("/applications", payload);
      toast.success("Candidature créée");
      setCreateOpen(false);
      setForm(emptyForm());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Erreur");
    } finally {
      setCreating(false);
    }
  };

  const bulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Supprimer définitivement ${selected.length} candidature${selected.length > 1 ? "s" : ""} ? Cette action est irréversible.`)) return;
    try {
      const r = await api.post("/admin/applications/bulk-delete", { ids: selected });
      toast.success(`${r.data.deleted} candidature${r.data.deleted > 1 ? "s" : ""} supprimée${r.data.deleted > 1 ? "s" : ""}`);
      load();
    } catch { toast.error("Erreur lors de la suppression"); }
  };

  // Sprint 92o — Bulk archive / reject
  const bulkStatus = async (action) => {
    if (selected.length === 0) return;
    const labels = { archive: "archiver", reject: "refuser", approve: "approuver" };
    const label = labels[action] || action;
    if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} ${selected.length} candidature${selected.length > 1 ? "s" : ""} ?`)) return;
    try {
      const r = await api.post("/admin/applications/bulk-status", { ids: selected, action });
      toast.success(`${r.data.updated} candidature${r.data.updated > 1 ? "s" : ""} ${action === "reject" ? "refusée" : action === "approve" ? "approuvée" : "archivée"}${r.data.updated > 1 ? "s" : ""}`);
      load();
    } catch { toast.error(`Erreur lors de l'action « ${label} »`); }
  };

  // Sprint 92af — Bulk "please book a call" reminder (uses admin's calendar URL).
  const bulkCalendarReminder = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Envoyer le lien de mon calendrier à ${selected.length} candidat${selected.length > 1 ? "s" : ""} ?\n\nEmail multilingue (FR/EN/ES/IT/PT auto).`)) return;
    try {
      const r = await api.post("/admin/applications/send-calendar-reminder", { ids: selected });
      const s = r.data.sent || 0;
      toast.success(`${s} email${s > 1 ? "s" : ""} envoyé${s > 1 ? "s" : ""}${r.data.skipped_no_email ? ` (${r.data.skipped_no_email} sans email)` : ""}`);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi");
    }
  };

  const statusBadge = (s) => ({
    new: "bg-blue-50 text-blue-700", submitted: "bg-blue-50 text-blue-700", review: "bg-amber-50 text-amber-700",
    approved: "bg-green-50 text-green-700", rejected: "bg-red-50 text-red-700",
  }[s] || "bg-neutral-100 text-neutral-700");

  return (
    <PortalLayout sidebar="admin">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl">Candidatures</h1>
          <p className="mt-1 text-neutral-600">Pipeline d&apos;adhésion — de la candidature reçue au membre actif. L&apos;onglet « Archivées » regroupe les dossiers payés et terminés.</p>
        </div>
        <div className="flex items-center gap-2">
        <button onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-4 py-2 text-xs font-bold text-white hover:bg-wine-600" data-testid="application-new-btn">
          <Plus className="h-3.5 w-3.5" /> Nouvelle candidature
        </button>
        </div>
      </div>

      {createOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setCreateOpen(false)}>
          <form onSubmit={submitCreate} className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="application-create-modal">
            <div className="flex items-center justify-between border-b border-neutral-100 pb-3">
              <h3 className="text-xl font-bold text-navy"><Plus className="mr-1 inline h-5 w-5 text-wine-500" /> Nouvelle candidature</h3>
              <button type="button" onClick={() => setCreateOpen(false)} className="text-neutral-400 hover:text-neutral-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-[10px] font-bold uppercase text-neutral-500">Société *</label>
                <input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-form-company" />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Prénom</label>
                  <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-form-first-name" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Nom</label>
                  <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-form-last-name" />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Email *</label>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-form-email" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Téléphone</label>
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-form-phone" />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Pôle</label>
                  <select value={form.pole} onChange={(e) => setForm({ ...form, pole: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-form-pole">
                    <option value="">— Choisir —</option>
                    <option value="producer">Producteur</option>
                    <option value="vintech">Wine-Tech</option>
                    <option value="distributor">Distributeur</option>
                    <option value="service">Services</option>
                    <option value="institutional">Institutionnel</option>
                    <option value="press">Presse</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Catégorie</label>
                  <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-form-category" placeholder="Ex. cat-startup, cat-pme..." />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Site web</label>
                  <input type="url" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-form-website" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">LinkedIn</label>
                  <input type="url" value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} placeholder="https://linkedin.com/..." className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-form-linkedin" />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">CA annuel (€)</label>
                  <input type="number" value={form.annual_revenue} onChange={(e) => setForm({ ...form, annual_revenue: e.target.value })} placeholder="Ex. 250000" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-form-revenue" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Pays</label>
                  <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-form-country" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-neutral-500">Description courte</label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Activité de l'entreprise, motivation à rejoindre..." className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-form-description" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2 border-t border-neutral-100 pt-4">
              <button type="button" onClick={() => setCreateOpen(false)} className="rounded-full border border-neutral-300 px-4 py-2 text-sm">Annuler</button>
              <button type="submit" disabled={creating} className="rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="app-form-submit">{creating ? "Création..." : "Créer la candidature"}</button>
            </div>
          </form>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        {STATUSES.map((s) => {
          const count = apps.filter((a) => matchTab(a, s.id)).length;
          return (
            <button key={s.id} onClick={() => { setFilter(s.id); setSelected([]); }} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold ${filter === s.id ? "bg-wine-500 text-white" : "bg-white text-neutral-600 border border-neutral-200 hover:border-wine-300"}`} data-testid={`apps-filter-${s.id}`}>
              {s.l}
              <span className={`inline-flex min-w-[20px] justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ${filter === s.id ? "bg-white/20 text-white" : "bg-neutral-100 text-neutral-600"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {selected.length > 0 && (
        <div className="mt-6 flex items-center justify-between rounded-2xl border-2 border-rose-300 bg-rose-50/60 p-3" data-testid="apps-bulk-toolbar">
          <span className="text-sm font-semibold text-rose-800">{selected.length} candidature{selected.length > 1 ? "s" : ""} sélectionnée{selected.length > 1 ? "s" : ""}</span>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setBulkEmailOpen(true)} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-wine-700" data-testid="apps-bulk-email" title="Envoyer un email (template + langue auto)">
              <Mail className="h-3 w-3" /> Email (template)
            </button>
            <button onClick={bulkCalendarReminder} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-emerald-700" data-testid="apps-bulk-calendar" title="Envoyer le lien de mon calendrier pour prendre RDV (multilingue auto)">
              <CalendarClock className="h-3 w-3" /> Envoyer mon calendrier
            </button>
            {filter !== "archived" && (
              <>
                <button onClick={() => bulkStatus("reject")} className="inline-flex items-center gap-1.5 rounded-full bg-red-500 px-4 py-1.5 text-xs font-bold text-white hover:bg-red-600" data-testid="apps-bulk-reject" title="Refuser & archiver la sélection">
                  <X className="h-3 w-3" /> Refuser
                </button>
                <button onClick={() => bulkStatus("archive")} className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-4 py-1.5 text-xs font-bold text-white hover:bg-amber-600" data-testid="apps-bulk-archive" title="Archiver sans changer le statut">
                  <Archive className="h-3 w-3" /> Archiver
                </button>
              </>
            )}
            <button onClick={() => setSelected([])} className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs font-semibold text-neutral-600 hover:bg-neutral-50" data-testid="apps-bulk-clear">Désélectionner</button>
            <button onClick={bulkDelete} className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-rose-700" data-testid="apps-bulk-delete">
              <Trash2 className="h-3 w-3" /> Supprimer
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 overflow-hidden rounded-2xl border border-neutral-100 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-wine-50/40 text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-3 py-3 w-8"><input type="checkbox" checked={allChecked} onChange={toggleSelectAll} data-testid="apps-bulk-select-all" /></th>
              <th className="px-5 py-3 text-left">Entreprise</th><th className="px-5 py-3 text-left">Contact</th><th className="px-5 py-3 text-left">Pays</th><th className="px-5 py-3 text-left">Catégorie</th><th className="px-5 py-3 text-left">Source</th><th className="px-5 py-3 text-left">RDV</th><th className="px-5 py-3 text-left">Statut</th><th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((a) => (
              <tr key={a.id} className="border-t border-neutral-100 hover:bg-wine-50/30" data-testid={`app-row-${a.id}`}>
                <td className="px-3 py-3 w-8"><input type="checkbox" checked={selected.includes(a.id)} onChange={() => toggleSelect(a.id)} data-testid={`apps-bulk-select-${a.id}`} /></td>
                <td className="px-5 py-4 font-semibold text-neutral-900">
                  <Link to={`/admin/applications/${a.id}`} className="hover:text-wine-700" data-testid={`app-detail-link-${a.id}`}>{a.company_name}</Link>
                  {a.duplicate_warning && (
                    <Link
                      to={`/admin/companies/${a.duplicate_warning.existing_company_id}`}
                      className="mt-1 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-800 hover:bg-amber-100"
                      title={`Doublon potentiel — match sur ${a.duplicate_warning.matched_on}. Fiche existante : ${a.duplicate_warning.existing_company_name} (${a.duplicate_warning.existing_company_status || "?"})`}
                      data-testid={`app-dup-warning-${a.id}`}
                    >
                      ⚠️ Doublon ({a.duplicate_warning.matched_on})
                    </Link>
                  )}
                </td>
                <td className="px-5 py-4"><div className="text-neutral-800">{a.contact_name}</div><div className="text-xs text-neutral-500">{a.email}</div></td>
                <td className="px-5 py-4 text-neutral-700">{a.country}</td>
                <td className="px-5 py-4 text-neutral-700">{a.category}</td>
                <td className="px-5 py-4">
                  {a.source === "fillout" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700" data-testid={`app-source-${a.id}`} title="Soumise via le formulaire Fillout">📝 Fillout</span>
                  ) : a.source ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-semibold text-neutral-700" data-testid={`app-source-${a.id}`}>{a.source}</span>
                  ) : <span className="text-xs text-neutral-400">—</span>}
                </td>
                <td className="px-5 py-4 text-xs">
                  {a.meeting_date ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700" data-testid={`app-meeting-${a.id}`} title={a.meeting_date}>
                      📅 {new Date(a.meeting_date).toLocaleDateString("fr", { day: "2-digit", month: "short" })} {new Date(a.meeting_date).toLocaleTimeString("fr", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  ) : <span className="text-neutral-400">—</span>}
                </td>
                <td className="px-5 py-4"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadge(a.status)}`}>{a.status}</span></td>
                <td className="px-5 py-4 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => setEditApp(a)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-600 hover:bg-blue-50 hover:text-blue-700" title="Modifier / enrichir" data-testid={`app-edit-${a.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                    {a.archived || a.status === "paid" || a.status === "active" ? (
                      <>
                        <button onClick={() => unarchive(a.id)} className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100" data-testid={`app-unarchive-${a.id}`}>Désarchiver</button>
                        <button onClick={() => del(a)} className="grid h-7 w-7 place-items-center rounded-full text-rose-600 hover:bg-rose-50" title="Supprimer définitivement" data-testid={`app-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </>
                    ) : (
                      <>
                        {["invoice_draft", "billing_received", "invoice_sent"].includes(a.status) && (
                          <button onClick={() => sendReminder(a)} className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100" title={a.last_reminder_at ? `Dernière relance : ${new Date(a.last_reminder_at).toLocaleDateString("fr")}` : "Envoyer un email de rappel paiement"} data-testid={`app-remind-${a.id}`}>Relancer{a.reminders_count ? ` (${a.reminders_count})` : ""}</button>
                        )}
                        <button onClick={() => upd(a.id, "approved")} className="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700 hover:bg-green-100" data-testid={`app-approve-${a.id}`}>Approuver</button>
                        <button onClick={() => upd(a.id, "rejected")} className="rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-100" data-testid={`app-reject-${a.id}`}>Refuser</button>
                        <button onClick={() => del(a)} className="grid h-7 w-7 place-items-center rounded-full text-rose-600 hover:bg-rose-50" title="Supprimer définitivement (test cleanup)" data-testid={`app-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={9} className="px-5 py-10 text-center text-sm text-neutral-500">{filter === "archived" ? "Aucune candidature archivée." : "Aucune candidature dans cet onglet."}</td></tr>}
          </tbody>
        </table>
      </div>

      <BulkEmailModal
        open={bulkEmailOpen}
        onClose={() => setBulkEmailOpen(false)}
        entity="applications"
        ids={selected}
        typeFilter="application"
        onSent={() => { setSelected([]); load(); }}
      />

      {editApp && (
        <ApplicationEditModal
          application={editApp}
          onClose={() => setEditApp(null)}
          onSaved={() => { setEditApp(null); load(); }}
        />
      )}
    </PortalLayout>
  );
}

// ============================== APPLICATION EDIT MODAL ==============================

function ApplicationEditModal({ application, onClose, onSaved }) {
  const [form, setForm] = useState({
    company_name: application.company_name || "",
    first_name: application.first_name || "",
    last_name: application.last_name || "",
    contact_name: application.contact_name || "",
    email: application.email || "",
    phone: application.phone || "",
    job_title: application.job_title || "",
    website: application.website || "",
    linkedin: application.linkedin || "",
    pole: application.pole || "",
    category: application.category || "",
    country: application.country || "France",
    description: application.description || "",
    motivation: application.motivation || "",
    innovation_types: application.innovation_types || "",
    interests: application.interests || "",
    annual_revenue: application.annual_revenue ?? "",
    notes: application.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.annual_revenue === "" || payload.annual_revenue === null) delete payload.annual_revenue;
      else payload.annual_revenue = parseFloat(payload.annual_revenue);
      await api.patch(`/applications/${application.id}`, payload);
      toast.success("Candidature mise à jour");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <form onSubmit={submit} className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="application-edit-modal">
        <div className="flex items-center justify-between border-b border-neutral-100 pb-3">
          <h3 className="text-xl font-bold text-navy"><Edit3 className="mr-1 inline h-5 w-5 text-wine-500" /> Modifier &amp; enrichir la candidature</h3>
          <button type="button" onClick={onClose} className="text-neutral-400"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="text-[10px] font-bold uppercase text-neutral-500">Société *</label>
            <input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-edit-company" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Prénom</label>
            <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-edit-first-name" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Nom</label>
            <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-edit-last-name" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Email</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-edit-email" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Téléphone</label>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-edit-phone" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Fonction</label>
            <input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-edit-job" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Pôle</label>
            <select value={form.pole} onChange={(e) => setForm({ ...form, pole: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-edit-pole">
              <option value="">— Choisir —</option>
              <option value="producer">Producteur</option>
              <option value="vintech">Wine-Tech</option>
              <option value="distributor">Distributeur</option>
              <option value="service">Services</option>
              <option value="institutional">Institutionnel</option>
              <option value="press">Presse</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Catégorie</label>
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-edit-category" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Site web</label>
            <input type="url" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-edit-website" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">LinkedIn</label>
            <input type="url" value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-edit-linkedin" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">CA annuel (€)</label>
            <input type="number" value={form.annual_revenue} onChange={(e) => setForm({ ...form, annual_revenue: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-edit-revenue" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Pays</label>
            <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-edit-country" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] font-bold uppercase text-neutral-500">Description / activité</label>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-edit-description" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] font-bold uppercase text-neutral-500">Motivation / intérêt</label>
            <textarea rows={2} value={form.motivation} onChange={(e) => setForm({ ...form, motivation: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-edit-motivation" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Innovation</label>
            <input value={form.innovation_types} onChange={(e) => setForm({ ...form, innovation_types: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-edit-innovation" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Intérêts</label>
            <input value={form.interests} onChange={(e) => setForm({ ...form, interests: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-edit-interests" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] font-bold uppercase text-neutral-500">Notes internes (admin)</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="app-edit-notes" />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2 border-t border-neutral-100 pt-4">
          <button type="button" onClick={onClose} className="rounded-full border border-neutral-300 px-4 py-2 text-sm">Annuler</button>
          <button type="submit" disabled={saving} className="rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="app-edit-submit">
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </form>
    </div>
  );
}
